import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/Auth/AuthContext";
import { userNotificationService, type UserNotification } from "@/services/userNotificationService";
import { replaceInternalOwnerEmails } from "@/utils/internalOwnerLabel";
import "./UserNotificationsBell.css";
import SmsNotificationSettings from "./SmsNotificationSettings";
import AssistantNotificationSummary from "../ProjectAssistant/AssistantNotificationSummary";

type UserNotificationsBellProps = {
  className?: string;
  variant?: "bell" | "panel";
  showAssistantSuggestions?: boolean;
};

const DEFAULT_LIMIT = 12;
const POLL_INTERVAL_MS = 60 * 1000;
const ISO_WITHOUT_TIMEZONE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?$/;

export function parseNotificationDate(value?: string | null) {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return null;
  }

  const candidate = /(?:Z|[+-]\d{2}:\d{2})$/i.test(normalized) || !ISO_WITHOUT_TIMEZONE_PATTERN.test(normalized)
    ? normalized
    : `${normalized}Z`;
  const date = new Date(candidate);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatNotificationTime(value?: string | null) {
  const date = parseNotificationDate(value);
  if (!date) {
    return "Just now";
  }

  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.max(0, Math.floor(diffMs / (1000 * 60)));

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function normalizeErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Unable to load notifications right now.";
}

const BellIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="user-notifications-bell__icon" aria-hidden="true">
    <path d="M15 18H5.5a1 1 0 0 1-.8-1.6l1.1-1.5a4 4 0 0 0 .8-2.4V9.7A5.4 5.4 0 0 1 12 4.3a5.4 5.4 0 0 1 5.4 5.4v2.8a4 4 0 0 0 .8 2.4l1.1 1.5a1 1 0 0 1-.8 1.6H15Z" />
    <path d="M10 20a2.3 2.3 0 0 0 4 0" />
  </svg>
);

const UserNotificationsBell: React.FC<UserNotificationsBellProps> = ({ className = "", variant = "bell", showAssistantSuggestions = false }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [markAllBusy, setMarkAllBusy] = useState(false);
  const [busyNotificationId, setBusyNotificationId] = useState<number | null>(null);
  const [available, setAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const isPersistentPanel = variant === "panel";

  const resetState = () => {
    setNotifications([]);
    setUnreadCount(0);
    setAvailable(true);
    setError(null);
    setIsOpen(false);
  };

  const loadNotifications = async (options: { silent?: boolean } = {}) => {
    if (!isAuthenticated || !user?.userDetails) {
      resetState();
      return;
    }

    if (!options.silent) {
      setIsLoading(true);
    }

    try {
      const result = await userNotificationService.list(DEFAULT_LIMIT, showAssistantSuggestions);
      setNotifications(result.notifications);
      setUnreadCount(result.unreadCount);
      setAvailable(result.available);
      setError(null);
    } catch (fetchError) {
      setError(normalizeErrorMessage(fetchError));
    } finally {
      if (!options.silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user?.userDetails) {
      resetState();
      return undefined;
    }

    void loadNotifications();

    const intervalId = window.setInterval(() => {
      void loadNotifications({ silent: true });
    }, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void loadNotifications({ silent: true });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, user?.userDetails, showAssistantSuggestions]);

  useEffect(() => {
    if (!isOpen || isPersistentPanel) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, isPersistentPanel]);

  const handleToggle = () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) {
      void loadNotifications();
    }
  };

  const markNotificationReadLocally = (notificationId: number) => {
    setNotifications((current) => current.map((item) => (
      item.userNotificationId === notificationId
        ? {
            ...item,
            isRead: true,
            readDate: item.readDate || new Date().toISOString(),
          }
        : item
    )));
    setUnreadCount((current) => Math.max(0, current - 1));
  };

  const handleNotificationClick = async (notification: UserNotification) => {
    if (!notification) {
      return;
    }

    if (!notification.isRead) {
      setBusyNotificationId(notification.userNotificationId);
      try {
        await userNotificationService.markRead(notification.userNotificationId);
        markNotificationReadLocally(notification.userNotificationId);
      } catch (markError) {
        setError(normalizeErrorMessage(markError));
      } finally {
        setBusyNotificationId(null);
      }
    }

    if (!isPersistentPanel) {
      setIsOpen(false);
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleMarkAllRead = async () => {
    if (!unreadCount) {
      return;
    }

    setMarkAllBusy(true);
    try {
      await userNotificationService.markAllRead();
      setNotifications((current) => current.map((item) => ({
        ...item,
        isRead: true,
        readDate: item.readDate || new Date().toISOString(),
      })));
      setUnreadCount(0);
    } catch (markError) {
      setError(normalizeErrorMessage(markError));
    } finally {
      setMarkAllBusy(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={`user-notifications-bell${isPersistentPanel ? " user-notifications-bell--panel" : ""} ${className}`.trim()}
    >
      {!isPersistentPanel ? (
        <button
          type="button"
          className="user-notifications-bell__trigger"
          aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
          title={unreadCount > 0 ? `${unreadCount} unread notifications` : "Notifications"}
          onClick={handleToggle}
          disabled={isLoading && !notifications.length}
        >
          <BellIcon />
          {unreadCount > 0 ? <span className="user-notifications-bell__badge" aria-hidden="true" /> : null}
        </button>
      ) : null}

      {isPersistentPanel || isOpen ? (
        <div className="user-notifications-bell__panel" role={isPersistentPanel ? "region" : "dialog"} aria-label="Notifications">
          <div className="user-notifications-bell__header">
            <div>
              <div className="user-notifications-bell__title">Notifications</div>
              <div className="user-notifications-bell__subtitle">
                {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
              </div>
            </div>
            <button
              type="button"
              className="user-notifications-bell__mark-all"
              onClick={handleMarkAllRead}
              disabled={markAllBusy || unreadCount === 0}
            >
              {markAllBusy ? "Marking..." : "Mark all read"}
            </button>
          </div>

          <div className="user-notifications-bell__body">
            <SmsNotificationSettings key={user?.userDetails} />
            {showAssistantSuggestions && user?.userDetails && <AssistantNotificationSummary key={`assistant:${user.userDetails}`} />}
            {error ? (
              <div className="user-notifications-bell__state user-notifications-bell__state--error">{error}</div>
            ) : !available ? (
              <div className="user-notifications-bell__state">Stored notifications are not available yet in this environment.</div>
            ) : isLoading && notifications.length === 0 ? (
              <div className="user-notifications-bell__state">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="user-notifications-bell__state">No notifications yet. New task assignments and accepted quotations will appear here.</div>
            ) : (
              <div className="user-notifications-bell__list">
                {notifications.map((notification) => (
                  <button
                    key={notification.userNotificationId}
                    type="button"
                    className={`user-notifications-bell__item${notification.isRead ? "" : " user-notifications-bell__item--unread"}`}
                    onClick={() => void handleNotificationClick(notification)}
                    disabled={busyNotificationId === notification.userNotificationId}
                  >
                    <div className="user-notifications-bell__item-header">
                      <span className="user-notifications-bell__item-title">{notification.title}</span>
                      <span className="user-notifications-bell__item-header-meta">
                        <span className="user-notifications-bell__item-meta">{formatNotificationTime(notification.createdDate)}</span>
                        {!notification.isRead ? <span className="user-notifications-bell__item-unread" aria-hidden="true" /> : null}
                      </span>
                    </div>
                    <div className="user-notifications-bell__item-message">{replaceInternalOwnerEmails(notification.message)}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserNotificationsBell;
